import React, { useContext } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Card, Typography, Button } from "antd";
import AppContext from "../../context/app.context";
import { IAppContext } from "../../interfaces/context.interfaces";
import { IFirebaseAuthUser } from "../../interfaces/authentication.interfaces";
import AuthenticationValidatorLogic from "../../logic/authenticationValidatorLogic";
import StatisticTable from "./StatisticTable";
import {
  canonicalObjectConfigKey,
  IObjectDashboardConfig,
  objectConfigs,
  objectIdHasDashboard,
  sviStatisticRows,
  sviStatisticSummary,
} from "../../services/svidashboard.mock";
import * as ROUTES from "../../constants/routes";
import {
  getSviObjectEventId,
  isDreamUser as checkIsDreamUser,
} from "../../constants/dream.constants";
import withAuthorization from "../HOC/with.authorization";

const { Title, Paragraph, Text } = Typography;

interface IRouteParams {
  objectId: string;
}

function shortObjectNameFromConfig(
  cfg: IObjectDashboardConfig | undefined,
): string | null {
  if (!cfg?.headerTitle) return null;
  const m = cfg.headerTitle.match(/^OBJECT:\s*\S+\s+(.+)\s*$/i);
  return m ? m[1].trim() : cfg.headerTitle.trim();
}

const ObjectStatisticPage: React.FC = () => {
  const { objectId } = useParams<IRouteParams>();
  const history = useHistory();
  const appContext: IAppContext = useContext(AppContext);
  const userEmail =
    appContext?.services?.firebaseService?.auth?.currentUser?.email ?? "";
  const isDreamUser = checkIsDreamUser(userEmail);

  const canonicalFromRoute = canonicalObjectConfigKey(objectId);
  const row = sviStatisticRows.find((r) => {
    const rowCanon =
      canonicalObjectConfigKey(r.objectId) ?? r.objectId.toUpperCase();
    if (canonicalFromRoute) {
      return rowCanon === canonicalFromRoute;
    }
    return r.objectId.toLowerCase() === objectId.trim().toLowerCase();
  });

  const configKey =
    row != null
      ? canonicalObjectConfigKey(row.objectId) ?? row.objectId.toUpperCase()
      : canonicalFromRoute ?? objectId.trim().toUpperCase();
  const objectConfig =
    configKey != null && objectConfigs[configKey]
      ? objectConfigs[configKey]
      : undefined;
  const objectDetails = objectConfig?.details;
  const hasDashboard =
    objectIdHasDashboard(row?.objectId) || objectIdHasDashboard(objectId);
  const fromStatsForQuery =
    row != null
      ? canonicalObjectConfigKey(row.objectId) ?? row.objectId
      : canonicalObjectConfigKey(objectId) ?? objectId.trim();
  const displayObjectName =
    shortObjectNameFromConfig(objectConfig) ?? row?.name ?? "";

  const handleBack = () => {
    history.push(ROUTES.SVI_DASHBOARD_STATS.route);
  };

  const rowsForTable = row ? [row] : [];

  return (
    <div style={{ padding: 24 }}>
      <Button style={{ marginBottom: 16 }} onClick={handleBack}>
        ← Back to statistics
      </Button>
      <Card>
        <Title level={3}>Object statistics</Title>
        {row ? (
          <>
            <Paragraph>
              <Text strong>Object ID:</Text> {row.objectId}
              <br />
              <Text strong>Name of object:</Text> {displayObjectName}
              <br />
              <Text strong>State:</Text> {objectDetails?.state ?? ""}
              <br />
              <Text strong>Type:</Text> {objectDetails?.type ?? ""}
              <br />
              <Text strong>Date of start:</Text> {objectDetails?.dateOfStart ?? ""}
              <br />
              <Text strong>Date end:</Text> {objectDetails?.dateEnd ?? ""}
              <br />
              <Text strong>Current % of fulfillment:</Text> {objectDetails?.currentFulfillment ?? ""}
              <br />
              <Text strong>Contractor:</Text> {objectDetails?.contractor ?? ""}
              <br />
              <Text strong>Last updated:</Text> {objectDetails?.lastUpdated ?? ""}
              <br />
              <Text strong>Location (lon/lat):</Text> {objectDetails?.location ?? ""}
            </Paragraph>
            <StatisticTable
              summary={sviStatisticSummary}
              rows={rowsForTable}
              normalWeightExpandedQuarterCells={isDreamUser}
            />
            {isDreamUser && (
              <Button
                type="primary"
                style={{ marginTop: 16 }}
                disabled={!hasDashboard}
                onClick={() => {
                  if (!hasDashboard) {
                    return;
                  }
                  history.push(
                    `${ROUTES.SVI_DASHBOARD.route}?eventId=${encodeURIComponent(
                      getSviObjectEventId(fromStatsForQuery),
                    )}&fromObjectStats=${encodeURIComponent(
                      fromStatsForQuery,
                    )}`,
                  );
                }}
              >
                Go to Dashboard
              </Button>
            )}
          </>
        ) : (
          <Paragraph>
            No statistics available for object with ID <Text code>{objectId}</Text>.
          </Paragraph>
        )}
      </Card>
    </div>
  );
};

const condition = (authUser: IFirebaseAuthUser) =>
  AuthenticationValidatorLogic.isDreamUser(authUser);

export default withAuthorization(condition)(ROUTES.MAP.route)(ObjectStatisticPage);
